import { useEffect, useMemo, useState } from 'react';
import type {
  FetchRecommendationsDataArgs,
  RecommendationsServingConfig,
} from '@noths/polaris-client-listings-api';
import {
  fetchRecommendationsData,
  ListingsRequestAndFilterBuilder,
  ListingsRequestTextFilterBuilder,
  RecommendationsRequestBuilder,
} from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { MAINLAND_UK_DELIVERY_ZONE } from '@noths/polaris-client-localisation';
import { generateRandomString, isBrowser } from '@noths/polaris-client-utils';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { parseCookies } from 'nookies';

import { isProd } from 'src/environment';
import type { TrackingProductData } from 'src/tracking/common/products';
import { logger } from 'src/utils/serverLogger';
import { transformRecommendationsProductToProductCarouselItem } from '../transformer';
import { transformRecommendationsServiceProductToTrackingProductData } from '../utils/transformRecommendationsServiceProductToTrackingProductData';

const createInStockFilter = () => {
  const inStockFilter = new ListingsRequestTextFilterBuilder('availability', ['IN_STOCK']).build();
  const filterCollection = [inStockFilter];
  return new ListingsRequestAndFilterBuilder().filters(filterCollection).build();
};

export interface UseListingsRecommendationsProps {
  currency: CurrencyCode;
  /**
   * Optional because it genuinely can be unknown: a visitor arriving with no
   * locale header and no `delivery_zone` cookie has none. What that means is
   * decided once, where the request is built.
   */
  deliveryZone?: DeliveryZoneCode;
  servingConfig: RecommendationsServingConfig;
}

type CreateRecommendationsParametersProps = Pick<
  UseListingsRecommendationsProps,
  'deliveryZone' | 'servingConfig'
> & {
  currencyCode: CurrencyCode;
  userId?: string;
  visitorId: string;
};

const createRecommendationsParameters = ({
  currencyCode,
  deliveryZone,
  servingConfig,
  userId,
  visitorId,
}: CreateRecommendationsParametersProps): FetchRecommendationsDataArgs => {
  const requestBuilder = new RecommendationsRequestBuilder();
  requestBuilder.currency(currencyCode);
  /* The zone scopes the `delivery` option the API resolves per product, so it is
     sent rather than applied to the response. Omitting it is not neutral: the API
     reads an absent zone as mainland UK and would quote a non-UK customer a UK
     price. Mainland UK for an unknown zone is the same choice the PLP grid's
     `adaptStoreToRequestBody` makes, so the two surfaces agree about a visitor we
     know nothing about. */
  requestBuilder.deliveryZone(deliveryZone ?? MAINLAND_UK_DELIVERY_ZONE);
  requestBuilder.eventType('home-page-view');
  requestBuilder.pageSize(10);
  const filter = createInStockFilter();
  requestBuilder.filter(filter);
  const body = requestBuilder.build();

  const clientSide = isBrowser();
  return {
    body,
    clientSide,
    env: isProd() ? 'prod' : 'qa',
    servingConfig,
    timeout: clientSide ? 15000 : 5500,
    userId,
    visitorId,
  };
};

export const useListingsRecommendations = ({
  currency,
  deliveryZone,
  servingConfig,
}: UseListingsRecommendationsProps) => {
  const [recommendedProducts, setRecommendedProducts] = useState<ProductCarouselItem[] | null>(
    null,
  );

  const [productsToTrack, setProductsToTrack] = useState<TrackingProductData[]>([]);

  const { _ga: googleAnalyticsVisitorId, cognitoId } = parseCookies();

  const visitorId = useMemo(() => {
    return googleAnalyticsVisitorId ?? generateRandomString(10);
  }, [googleAnalyticsVisitorId]);

  const currencyCode = useMemo(() => {
    return currency as CurrencyCode;
  }, [currency]);

  const userId = useMemo(() => {
    return cognitoId;
  }, [cognitoId]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setRecommendedProducts(null);
        const fetchParams = createRecommendationsParameters({
          currencyCode,
          deliveryZone,
          servingConfig,
          userId: userId ?? undefined,
          visitorId,
        });

        const responseData = await fetchRecommendationsData(fetchParams);

        if ('message' in responseData) {
          setRecommendedProducts([]);
          logger.error('Error getting recommendations from listings-api:', responseData.message);
          return;
        }

        /* Captured once for the whole response rather than left to the
           transformer's per-call default, so every card in the carousel counts
           its arrival from the same instant. Otherwise a carousel mapped across
           midnight could show two different dates for the same lead time. */
        const now = new Date();

        const adaptedData = responseData.products.map((product) =>
          transformRecommendationsProductToProductCarouselItem(product, {
            currency: currencyCode,
            now,
          }),
        );
        setRecommendedProducts(adaptedData);
        const adapterProductsToTrack = responseData.products.map((p) =>
          transformRecommendationsServiceProductToTrackingProductData(p),
        );
        setProductsToTrack(adapterProductsToTrack);
      } catch (error) {
        setRecommendedProducts([]);
        logger.error('Exception thrown while getting recommendations from listings-api:', error);
      }
    };

    fetchProducts();
  }, [currencyCode, deliveryZone, servingConfig, userId, visitorId]);

  return {
    recommendedProducts,
    productsToTrack,
    currencyCode,
  };
};
